/**
 * S29 — Settings · Users & Roles (`/settings/users`).
 * IdP-synced user list with role + MFA + last-login. Synced via SCIM.
 */
import React, { useMemo, useState } from "react";
import { Users, ShieldCheck, AlertCircle, Search } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTenantData } from "@/contexts/TenantModeContext";
import { useEntityList } from "@/lib/useEntity";
import { SyncedIndicator } from "@/components/data/SyncedIndicator";
import { Flywheel } from "@/components/brand/Flywheel";
import { KpiTile } from "@/pages/dashboard/components/DashboardPrimitives";
import { cn } from "@/lib/utils";

export default function UsersAndRoles() {
  const { user } = useAuth();
  const { isCustomer } = useTenantData();
  const {
    items: users,
    loading,
    error,
    lastSyncedAt,
    refetch,
  } = useEntityList("/api/users");
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return users.filter((u) => {
      if (role && u.role !== role) return false;
      if (term && !`${u.name} ${u.email} ${u.role} ${u.department}`.toLowerCase().includes(term)) return false;
      return true;
    });
  }, [users, q, role]);

  if (loading && users.length === 0) return <UrSkeleton />;
  if (error) return <UrError message={error.message} onRetry={refetch} />;
  if (isCustomer && !users.length) return <Empty userName={user?.name?.split(" ")[0]} />;

  const roles = Array.from(new Set(users.map((u) => u.role)));
  const active = users.filter((u) => u.status === "active").length;
  const mfaCoverage = users.length ? Math.round((users.filter((u) => u.mfa).length / users.length) * 100) : 0;

  return (
    <div className="relative px-6 lg:px-8 py-6 anim-fade-up" data-testid="users-roles">
      <div className="absolute -right-24 -top-12 pointer-events-none"><Flywheel size={300} opacity={0.05} /></div>

      <p className="eyebrow">Settings · Users & Roles</p>
      <h1 className="text-display-md text-ink-primary mt-1">Users & Roles</h1>
      <p className="text-ink-secondary mt-1 max-w-2xl">SCIM-synced from your Identity Provider. Roles map to the ALGOS RBAC matrix and govern every mutating action.</p>
      <div className="mt-2">
        <SyncedIndicator
          lastSyncedAt={lastSyncedAt}
          loading={loading}
          onRefetch={refetch}
          testId="users-roles-synced"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <KpiTile label="Provisioned users" value={users.length.toString()}  delta="SCIM-synced"          deltaTone="neutral" hint="from your IdP"             icon={Users}        tone="tralenex" testId="ur-kpi-total" />
        <KpiTile label="Active"            value={active.toString()}        delta={`${users.length - active} not active`} deltaTone={active === users.length ? "up" : "neutral"} hint="last 30 days"  icon={ShieldCheck}  tone="low"      testId="ur-kpi-active" />
        <KpiTile label="MFA coverage"      value={`${mfaCoverage}%`}        delta={mfaCoverage >= 90 ? "Strong" : "Lift coverage"} deltaTone={mfaCoverage >= 90 ? "up" : "down"} hint="across all users" icon={ShieldCheck} tone="gold" testId="ur-kpi-mfa" />
        <KpiTile label="Distinct roles"    value={roles.length.toString()}  delta="mapped to RBAC"        deltaTone="neutral" hint="see rbac.js"               icon={AlertCircle}  tone="risk"     testId="ur-kpi-roles" />
      </div>

      <div className="mt-6 surface-card shadow-e1 overflow-hidden" data-testid="ur-panel">
        <div className="px-4 py-3 border-b border-border-subtle flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-disabled" />
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, department…" data-testid="ur-search"
              className="w-full pl-8 pr-3 h-9 rounded-md border border-border-subtle bg-white text-sm placeholder:text-ink-disabled focus:outline-none focus:border-tralenex-500" />
          </div>
          <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="ur-role-filter" className="h-9 rounded-md border border-border-subtle bg-white text-sm px-2">
            <option value="">All roles</option>
            {roles.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <span className="ml-auto text-meta text-ink-secondary tabular-nums">{filtered.length} of {users.length}</span>
        </div>
        <table className="w-full text-sm" data-testid="ur-table">
          <thead className="bg-inset/40 text-ink-secondary text-meta uppercase tracking-[0.06em]">
            <tr>{["User","Email","Role","Department","Status","MFA","Last login"].map((h) => (
              <th key={h} className="px-3 py-2 text-left font-semibold whitespace-nowrap">{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {filtered.map((u) => (
              <tr key={u.id} className="border-t border-border-subtle hover:bg-inset/30" data-testid={`ur-row-${u.id}`}>
                <td className="px-3 py-2 text-ink-primary font-medium whitespace-nowrap">{u.name}</td>
                <td className="px-3 py-2 text-meta font-mono text-ink-secondary whitespace-nowrap">{u.email}</td>
                <td className="px-3 py-2 text-ink-primary whitespace-nowrap">{u.role}</td>
                <td className="px-3 py-2 text-meta text-ink-secondary whitespace-nowrap">{u.department}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <span className={cn("inline-block px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-[0.06em]",
                    u.status === "active" ? "bg-risk-low/10 text-risk-low" : u.status === "invited" ? "bg-gold-300/15 text-gold-700" : "bg-risk-high/10 text-risk-high")}>
                    {u.status}
                  </span>
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-meta uppercase tracking-[0.06em] font-bold">
                  {u.mfa ? <span className="text-risk-low">enforced</span> : <span className="text-risk-high">missing</span>}
                </td>
                <td className="px-3 py-2 text-meta font-mono text-ink-secondary tabular-nums whitespace-nowrap">{u.lastLogin}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Empty({ userName }) {
  return (
    <div className="relative min-h-[calc(100vh-112px)] px-6 lg:px-10 py-10 overflow-hidden" data-testid="ur-customer-empty">
      <div className="absolute -right-40 -bottom-40 pointer-events-none"><Flywheel size={620} opacity={0.04} /></div>
      <div className="relative max-w-2xl">
        <p className="eyebrow">Settings · Users & Roles</p>
        <h1 className="text-display-lg text-ink-primary mt-1">{userName ? `Welcome, ${userName}.` : "Welcome."}</h1>
        <p className="text-ink-secondary mt-3 text-[15px]">Connect your IdP via SCIM to populate this directory automatically.</p>
      </div>
    </div>
  );
}

function UrSkeleton() {
  return (
    <div className="px-6 lg:px-8 py-6 anim-fade-up" data-testid="users-roles-loading">
      <div className="h-7 w-72 rounded-md bg-inset/60 animate-pulse" />
      <div className="mt-2 h-4 w-96 rounded-md bg-inset/40 animate-pulse" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-24 rounded-md surface-card animate-pulse bg-inset/30" />
        ))}
      </div>
      <div className="mt-6 h-[400px] rounded-md surface-card animate-pulse bg-inset/20" />
    </div>
  );
}

function UrError({ message, onRetry }) {
  return (
    <div className="px-6 lg:px-8 py-6" data-testid="users-roles-error">
      <div className="surface-card shadow-e1 p-5 border-l-[3px] border-l-risk-high">
        <p className="eyebrow text-risk-high">Settings · Users & Roles</p>
        <h1 className="text-h2 text-ink-primary mt-1">Couldn&apos;t load the directory</h1>
        <p className="mt-2 text-meta text-ink-secondary">{message || "Network error — please retry."}</p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-4 inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-tralenex-600 text-white text-sm font-semibold hover:bg-tralenex-700"
          data-testid="users-roles-retry"
        >
          Retry
        </button>
      </div>
    </div>
  );
}
